import _get from "lodash/get";
import { formatDate } from "./dateUtils";
import { http } from "@api/base";

const borrowerInfoCache = new Map();
const fetchBorrowerInfoFromAPI = async (loanPid) => {
  if (!loanPid) return null;

  if (borrowerInfoCache.has(loanPid)) {
    return borrowerInfoCache.get(loanPid);
  }

  try {
    const response = await http.get(`/api/loans/${loanPid}/borrower-info`);
    const borrowerInfo = response.data;

    borrowerInfoCache.set(loanPid, borrowerInfo);

    return borrowerInfo;
  } catch (error) {
    borrowerInfoCache.set(loanPid, null);
    return null;
  }
};

const fetchBulkBorrowerInfo = async (loanPids) => {
  if (!loanPids || loanPids.length === 0) return {};

  const uncachedPids = loanPids.filter((pid) => !borrowerInfoCache.has(pid));

  let bulkResults = {};

  if (uncachedPids.length > 0) {
    try {
      const response = await http.post("/api/loans/borrower-info/bulk", {
        loan_pids: uncachedPids,
      });
      bulkResults = response.data;

      uncachedPids.forEach((pid) => {
        const result = bulkResults[pid] || null;
        borrowerInfoCache.set(pid, result);
      });
    } catch (error) {
      uncachedPids.forEach((pid) => borrowerInfoCache.set(pid, null));
    }
  }

  const allResults = {};
  loanPids.forEach((pid) => {
    const cached = borrowerInfoCache.get(pid);
    allResults[pid] = cached || bulkResults[pid] || null;
  });

  return allResults;
};

export const getFullBorrowerInfoSync = (loan) => {
  if (!loan || typeof loan !== "object") {
    return {
      displayName: "Unknown Borrower",
      year: null,
      program: null,
      programCode: null,
      college: null,
      semester: null,
      email: null,
      displayProgram: "N/A",
      programYear: "N/A",
    };
  }

  const loanPid = loan.metadata?.pid || loan.pid || loan.id;
  const cachedBorrowerInfo = borrowerInfoCache.get(loanPid);

  const displayName =
    _get(loan, "metadata.patron.name") ||
    (cachedBorrowerInfo
      ? `${cachedBorrowerInfo.first_name || ""} ${
          cachedBorrowerInfo.last_name || ""
        }`.trim()
      : null) ||
    "Unknown Borrower";

  const program = cachedBorrowerInfo?.program || null;
  const programCode = cachedBorrowerInfo?.program_code || null;
  const year = cachedBorrowerInfo?.year || null;
  const college = cachedBorrowerInfo?.college || null;
  const semester = cachedBorrowerInfo?.semester || null;
  const email =
    _get(loan, "metadata.patron.email") ||
    cachedBorrowerInfo?.contact_no ||
    null;
  const displayProgram = programCode || program || "N/A";

  let programYear = "N/A";
  if (programCode && year) {
    programYear = `${programCode} - ${year}`;
  } else if (program && year) {
    programYear = `${program} - ${year}`;
  } else if (programCode) {
    programYear = programCode;
  } else if (program) {
    programYear = program;
  } else if (year) {
    programYear = `Year ${year}`;
  }

  return {
    displayName,
    year,
    program,
    programCode,
    college,
    semester,
    email,
    displayProgram,
    programYear,
  };
};

export const preloadBorrowerInfo = async (loans) => {
  if (!Array.isArray(loans)) return;

  const loanPids = loans
    .map((loan) => loan.metadata?.pid || loan.pid || loan.id)
    .filter((pid) => pid && !borrowerInfoCache.has(pid));

  if (loanPids.length > 0) {
    await fetchBulkBorrowerInfo(loanPids);
  }
};

export const getLoanDate = (loan, dateType = "start") => {
  if (typeof loan === "string") {
    return dateType === "start" ? loan : "";
  }

  if (typeof loan !== "object" || loan === null) {
    return "";
  }

  if (dateType === "start") {
    return (
      _get(loan, "metadata.start_date") ||
      _get(loan, "metadata.request_start_date") ||
      ""
    );
  } else if (dateType === "due") {
    return (
      _get(loan, "metadata.end_date") ||
      _get(loan, "metadata.request_expire_date") ||
      ""
    );
  }

  return "";
};

export const isLoanReturned = (loan) => {
  if (typeof loan === "string" || typeof loan !== "object" || loan === null) {
    return false;
  }

  const state = _get(loan, "metadata.state");
  const isReturnedByState = state === "ITEM_RETURNED";
  const isActiveByState = state === "ITEM_ON_LOAN";

  return isReturnedByState && !isActiveByState;
};

export const createLoanRecord = (loan) => {
  const borrowerInfo = getFullBorrowerInfoSync(loan);
  const checkOutDate = formatDate(getLoanDate(loan, "start"));
  const dueDate = formatDate(getLoanDate(loan, "due"));

  return {
    borrowerName: borrowerInfo.displayName,
    borrowerYear: borrowerInfo.year,
    borrowerProgram: borrowerInfo.program,
    borrowerCollege: borrowerInfo.college,
    borrowerSemester: borrowerInfo.semester,
    borrowerEmail: borrowerInfo.email,
    displayProgram: borrowerInfo.displayProgram,

    programYear: borrowerInfo.programYear,
    checkOutDate,
    dueDate,
    returnDate: dueDate,
    isReturned: true,
    source: "loanHistory",
  };
};

export const getReturnedLoanRecords = async (loanHistory = []) => {
  const records = [];

  if (!Array.isArray(loanHistory) || loanHistory.length === 0) {
    return records;
  }

  await preloadBorrowerInfo(loanHistory);

  loanHistory.forEach((loan) => {
    if (!isLoanReturned(loan)) {
      return;
    }

    const record = createLoanRecord(loan);
    const { borrowerName } = record;

    if (
      borrowerName &&
      borrowerName !== "Unknown Borrower" &&
      borrowerName !== "Current Borrower"
    ) {
      records.push(record);
    }
  });

  records.sort((a, b) => {
    const dateA = new Date(a.checkOutDate || 0);
    const dateB = new Date(b.checkOutDate || 0);
    return dateB - dateA;
  });

  return records;
};

export { fetchBorrowerInfoFromAPI, fetchBulkBorrowerInfo };
